import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AccessibleIcon, DropdownMenu, IconButton } from 'components/ds'
import { EllipsisVerticalIcon } from 'components/ui/icon/svg-icons'

import { useSelector } from 'hooks/use-redux'
import { useDispatch } from 'hooks/use-redux'

import { getConstanceSettings } from '@disco/state-management/selectors/user'
import {
  duplicateBrief,
  saveBrief,
} from '@disco/state-management/reducers/entities/briefs'
import { Brief } from '@disco/types/types/brief'
import { pushUINotification } from '@disco/utils/notifications'
import { HoverContext } from 'components/ui/hover-provider'
import { useReopenBrief } from '../../hooks/use-reopen-brief'
import { updateBriefInPanes } from '@disco/state-management/actions/ExplorerActions'
import mixpanelClient from '@disco/api/mixpanel'
import dayjs from '@disco/utils/dayjs'

type BriefActionMenuProps = {
  brief: Brief
  onOpenChange?: (open: boolean) => void
  hoverContext?: React.ContextType<typeof HoverContext>
  iconColor?: string
  editMode?: boolean
}

const BriefActionMenu: React.FC<BriefActionMenuProps> = ({
  brief,
  onOpenChange,
  hoverContext,
  iconColor = 'gray',
  editMode,
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const constanceSettings = useSelector(getConstanceSettings)
  const [closing, setClosing] = useState(false)
  const [redirectToShareModal, setRedirectToShareModal] = useState(false)

  const [menuOpen, setMenuOpen] = useState(false)

  const navigateToShareModal = () => {
    if (editMode) {
      navigate(`/brief-editor-v2/${brief.id}/share`)
    } else {
      navigate(`/briefs/${brief.id}/share`)
    }
    setRedirectToShareModal(false)
  }

  const {
    reopening,
    isDeadlineInPast,
    handleReopenBrief,
    deadlinePickerModal,
  } = useReopenBrief({
    brief,
    onSuccess: updatedBrief => {
      const initialDeadline = dayjs(brief.deadline)
      const newDeadline = dayjs(updatedBrief.deadline)
      const number_days_extended = initialDeadline.diff(newDeadline, 'day')
      mixpanelClient.sendPureEvent('Brief reopened', {
        Context: 'Briefs',
        brief_id: updatedBrief.id,
        deadline_extended: updatedBrief.deadline,
        days_extended: number_days_extended,
      })
      dispatch(updateBriefInPanes(updatedBrief))
      if (redirectToShareModal) {
        navigateToShareModal()
        setRedirectToShareModal(false)
      }
    },
    onCancel: () => {
      setRedirectToShareModal(false)
    },
  })

  useEffect(() => {
    // Keep hover state true as dropdown menu is using portal
    if (menuOpen && hoverContext?.setHover) {
      hoverContext.setHover(true)
    }
  }, [menuOpen, hoverContext])

  const briefWorkflowV2Enabled = constanceSettings?.BRIEF_WORKFLOW_V2 || false
  const canShareBrief = brief.publishing_status !== 'draft'

  const handleEditBrief = (briefId: number) => {
    if (briefId) {
      const url = briefWorkflowV2Enabled
        ? `/brief-editor-v2/${briefId}`
        : `/brief-editor/${briefId}/content`
      navigate(url)
    }
  }

  const handleDuplicate = async () => {
    const newBrief = await dispatch(duplicateBrief(brief.id)).unwrap()
    mixpanelClient.sendPureEvent('Brief duplicated', {
      Context: 'Briefs',
      original_brief_id: brief.id,
    })
    const editUrl = briefWorkflowV2Enabled
      ? `/brief-editor-v2/${newBrief.id}`
      : `/brief-editor/${newBrief.id}/content`
    navigate(editUrl)
  }

  const handleShare = () => {
    if (isDeadlineInPast) {
      // If brief is closed and deadline is in the past, require reopening before sharing
      setRedirectToShareModal(true)
      handleReopenBrief()
      return
    }
    navigateToShareModal()
  }

  const handleManageBriefAccess = () => {
    if (editMode) {
      navigate(`/brief-editor-v2/${brief.id}/access`)
    } else {
      navigate(`/briefs/${brief.id}/access`)
    }
  }

  const handleBriefSettings = () => {
    if (editMode) {
      navigate(`/brief-editor-v2/${brief.id}/settings`)
    } else {
      navigate(`/briefs/${brief.id}/settings`)
    }
  }

  const handleDelete = () => {
    if (brief.id) {
      if (editMode) {
        return navigate(`/brief-editor-v2/${brief.id}/delete`)
      }

      navigate(`/briefs/${brief.id}/delete`)
    }
  }

  const handleCloseBrief = async () => {
    if (!brief) return
    setClosing(true)
    try {
      const updatedBrief = await dispatch(
        saveBrief({ ...brief, submission_status: 'closed' }),
      ).unwrap()

      dispatch(updateBriefInPanes(updatedBrief))
      const briefSentAt = dayjs(brief.sentAt)
      const today = dayjs(new Date())
      const daysOpen = briefSentAt.diff(today, 'day')
      mixpanelClient.sendPureEvent('Brief closed', {
        Context: 'Briefs',
        brief_id: brief.id,
        submission_count: brief.response_count,
        days_open: daysOpen,
      })
      pushUINotification({
        message: 'Brief has been closed successfully',
        level: 'success',
      })
    } catch (error) {
      pushUINotification({
        message: 'Failed to close brief',
        level: 'error',
      })
    } finally {
      setClosing(false)
    }
  }

  const handleOpenChange = (open: boolean) => {
    setMenuOpen(open)
    if (hoverContext?.setHover && !open) {
      hoverContext.setHover(false)
    }
    onOpenChange?.(open)
  }

  //TODO: remove briefWorkflowV2Enabled check when V1 is deprecated
  return (
    <>
      <DropdownMenu.Root onOpenChange={handleOpenChange}>
        <DropdownMenu.Trigger>
          <IconButton variant="ghost" size="2">
            <AccessibleIcon label="Brief Actions">
              <EllipsisVerticalIcon size={24} color={iconColor} />
            </AccessibleIcon>
          </IconButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content variant="soft" align="end">
          {briefWorkflowV2Enabled && (
            <DropdownMenu.Item
              disabled={!canShareBrief}
              onClick={
                canShareBrief
                  ? () => {
                      handleShare()
                    }
                  : undefined
              }
            >
              Share
            </DropdownMenu.Item>
          )}
          {!editMode && (
            <DropdownMenu.Item onClick={() => handleEditBrief(brief.id)}>
              Edit brief
            </DropdownMenu.Item>
          )}
          <DropdownMenu.Item onClick={handleDuplicate}>
            Duplicate
          </DropdownMenu.Item>
          <DropdownMenu.Item onClick={handleBriefSettings}>
            Brief settings
          </DropdownMenu.Item>
          {briefWorkflowV2Enabled &&
            (brief.status === 'sent' || brief.sent_status) && (
              <DropdownMenu.Item onClick={handleManageBriefAccess}>
                Manage access
              </DropdownMenu.Item>
            )}
          {briefWorkflowV2Enabled &&
            brief?.submission_status === 'closed' &&
            brief?.publishing_status !== 'draft' && (
              <DropdownMenu.Item
                onClick={handleReopenBrief}
                disabled={reopening}
              >
                {reopening ? 'Reopening brief...' : 'Reopen brief'}
              </DropdownMenu.Item>
            )}
          {briefWorkflowV2Enabled &&
            brief?.submission_status !== 'closed' &&
            brief?.publishing_status !== 'draft' && (
              <DropdownMenu.Item onClick={handleCloseBrief} disabled={closing}>
                {closing ? 'Closing brief...' : 'Close brief'}
              </DropdownMenu.Item>
            )}
          <DropdownMenu.Item color="red" onClick={handleDelete}>
            Delete brief
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>
      {deadlinePickerModal}
    </>
  )
}

export default BriefActionMenu
